import { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import LayoutContainer from 'components/Common/LayoutContainer'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import Group from 'components/utils/Group'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import {
  OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import Heading from 'LuxKit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import useQueryParams from 'hooks/useQueryParams'
import GeoContext from 'contexts/geoContext'
import useOffers from 'hooks/Offers/useOffers'
import { isBedbankOffer, isLEOffer } from 'lib/offer/offerTypes'

const GOOGLE_AD_OFFER_TYPE: Array<App.OfferType> = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK]

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: ${rem(24)};
  padding-bottom: ${rem(16)};
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    filters,
  }
}

const connector = connect(mapState, null)

interface MappedStateProps {
  filters: App.OfferListFilters
}

interface Props extends MappedStateProps { }

function GoogleHotelAdPage({
  filters,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const offerList = useOfferList({
    ...filters,
    offerTypes: GOOGLE_AD_OFFER_TYPE,
    limit: 1,
    isStream: true,
  })

  const query = useQueryParams()
  const roomTypeId = query.get('roomTypeId')

  const [offers, offersFetching] = useOffers(offerList.offerIds)

  const offer = useMemo(() => {
    // find the offer that has the room type/room rate we're after
    return offers.find(offer => {
      if (isBedbankOffer(offer)) {
        // return the offer. no package match checking
        return offer
      }
      else {
        return isLEOffer(offer) && offer.packages.some(pkg => pkg.roomType?.id === roomTypeId)
      }
    })
  }, [offers, roomTypeId])

  const fetching = offersFetching || offerList.fetching

  const searchingMessage = intl.formatMessage({
    id: 'Rn5LvNxGah_iUbgQQJqh',
    defaultMessage: 'Searching for offers',
    description: 'Loading message shown while searching for Google Hotel Ad offers',
  })

  if (fetching) {
    return <StyledLayoutContainer>
      <Group direction="horizontal" gap={16} verticalAlign="center">
        <ColourfulLoadingAnimation size={8} />
        <Heading variant="heading5" as="span">{searchingMessage}</Heading>
      </Group>
    </StyledLayoutContainer>
  } else if (!fetching && (offerList.offerIds.length === 0 || !offer)) {
    return <Error404PageWithOffers />
  }

  // Validate offer data before redirect
  if (!offer) {
    throw new Error('No valid offer found for the given roomTypeId')
  }

  if (!offer.slug || !offer.id) {
    throw new Error('Invalid offer data: missing required fields (slug or id)')
  }

  if (!currentRegionCode) {
    throw new Error('Missing region code for redirect')
  }

  const redirectUrl = isBedbankOffer(offer) ?
    `/${currentRegionCode.toLowerCase()}/partner/${offer.slug}/${offer.id}` :
    `/${currentRegionCode.toLowerCase()}/offer/${offer.slug}/${offer.id}`

  return <RedirectWithStatus
    code={302}
    to={redirectUrl}
  />
}

export default connector(GoogleHotelAdPage)
